import { http, createConfig, createStorage } from "wagmi";
import {
  arbitrum,
  arbitrumNova,
  avalanche,
  base,
  blast,
  bob,
  boba,
  cyber,
  fantom,
  funkiMainnet,
  gnosis,
  ham,
  linea,
  lisk,
  mainnet,
  manta,
  mantle,
  mint,
  mode,
  optimism,
  polygon,
  polygonZkEvm,
  real,
  redstone,
  scroll,
  taiko,
  unichain,
  zksync,
  zora,
  polygonAmoy,
  berachain,
  ink,
  sepolia,
  baseSepolia,
  bsc,
  monad,
} from "wagmi/chains";
import { injected, walletConnect, metaMask } from "wagmi/connectors";
import { defineChain, fallback } from "viem";
import { glideLocalStorage } from "./glideLocalStorage";
import { logger } from "./logger";

// Define custom chains outside the config
const sxChain = defineChain({
  id: 4162,
  name: "SX",
  nativeCurrency: {
    decimals: 18,
    name: "SX",
    symbol: "SX",
  },
  rpcUrls: {
    default: {
      http: [
        "https://rpc-rollup.sx.technology/infra-partner-http/4cde6fdfad644b55a6f2d47744650572",
        "https://rpc-rollup.sx.technology",
      ],
    },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://explorerl2.sx.technology/",
    },
  },
});

const sxToronto = defineChain({
  id: 79479957,
  name: "SX Toronto Rollup",
  testnet: true,
  nativeCurrency: {
    decimals: 18,
    name: "SX",
    symbol: "SX",
  },
  rpcUrls: {
    default: {
      http: ["https://rpc-rollup.toronto.sx.technology"],
    },
  },
  blockExplorers: {
    default: {
      name: "Explorer",
      url: "https://explorerl2.toronto.sx.technology",
    },
  },
});

const hyperevmChain = defineChain({
  id: 999,
  name: "HyperEVM",
  nativeCurrency: {
    decimals: 18,
    name: "HYPE",
    symbol: "HYPE",
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.hyperliquid.xyz/evm"],
    },
  },
  blockExplorers: {
    default: {
      name: "HyperEVM Scan",
      url: "https://hyperevmscan.io",
    },
  },
});

const assetchainMainnet = defineChain({
  id: 42420,
  name: "AssetChain Mainnet",
  nativeCurrency: {
    decimals: 18,
    name: "Real World Asset",
    symbol: "RWA",
  },
  rpcUrls: {
    default: {
      http: ["https://mainnet-rpc.assetchain.org"],
    },
  },
  blockExplorers: {
    default: {
      name: "AssetChain Explorer",
      url: "https://scan.assetchain.org",
    },
  },
});

const rariChain = defineChain({
  id: 1380012617,
  name: "RARI Chain",
  nativeCurrency: {
    decimals: 18,
    name: "Ethereum",
    symbol: "ETH",
  },
  rpcUrls: {
    default: {
      http: ["https://mainnet.rpc.rarichain.org/http"],
    },
  },
  blockExplorers: {
    default: {
      name: "RARI Chain Explorer",
      url: "https://mainnet.explorer.rarichain.org",
    },
  },
});

const worldChain = defineChain({
  id: 480,
  name: "World Chain",
  nativeCurrency: {
    decimals: 18,
    name: "Ether",
    symbol: "ETH",
  },
  rpcUrls: {
    default: {
      http: ["https://worldchain-mainnet.g.alchemy.com/public"],
    },
  },
  blockExplorers: {
    default: {
      name: "Worldscan",
      url: "https://worldscan.org",
    },
  },
});

// Detect if mobile to configure MetaMask SDK appropriately
const isMobile =
  typeof navigator !== "undefined" &&
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    navigator.userAgent.toLowerCase(),
  );

// The Coinbase Wallet extension is discovered via EIP-6963 (rdns below) since
// the coinbaseWallet() connector was removed. Its `request` handler
// infinite-loops if probed automatically (mount-time reconnect, tab refocus).
// createWagmiConfig() strips it from config.connectors immediately after
// creation so WagmiProvider's default reconnectOnMount never sees it;
// restoreCoinbaseConnector() below adds it back after mount so it's still
// listed for manual connect. Callers doing bulk/manual reconnects (e.g.
// mobileWalletReconnect) must also exclude this id explicitly.
export const COINBASE_WALLET_RDNS = "com.coinbase.wallet";

const preferredRpcUrlsByChainId: Record<number, readonly string[]> = {
  [mainnet.id]: [
    "https://mainnet.gateway.tenderly.co/15aTN1reQzBDglX0xuHUrl",
    "https://eth-mainnet.g.alchemy.com/v2/95hQzf_-yYsZGaUjmXXF666hsOFxbr76",
  ],
  [sepolia.id]: [
    "https://eth-sepolia.g.alchemy.com/v2/oDJda-T28rDOobOiHqmIZdekF-ctWGps",
  ],
  [base.id]: [
    "https://old-quaint-frost.base-mainnet.quiknode.pro/81601660e3b7030268d396e5107e2136b0af95b3",
    "https://base-mainnet.g.alchemy.com/v2/34ne5eAzZvPJtrQH8yD_6pzFrEdgYhTZ",
    "https://base.gateway.tenderly.co/6teD3MXpeMt7RcORXsNz4Z",
  ],
  [baseSepolia.id]: [
    "https://base-sepolia.g.alchemy.com/v2/_DufEMzAwZsOanCQq9g1pFhxH09t5l5T",
  ],
  [arbitrum.id]: [
    "https://arb-mainnet.g.alchemy.com/v2/uTI2BxtQn5uqxbt3d9GhsIeMQJktxMnu",
    "https://arbitrum.gateway.tenderly.co/3oTdSOOK0ComPuz4VsPR4",
  ],
  [arbitrumNova.id]: [
    "https://arbnova-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [polygon.id]: [
    "https://polygon-mainnet.g.alchemy.com/v2/j177ywf6iCSBH6YD9iIgv9dGALy0tDY1",
    "https://polygon.gateway.tenderly.co/42t0xRLkQ15ArJlm9PNmrB",
  ],
  [polygonAmoy.id]: [
    "https://polygon-amoy.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [optimism.id]: [
    "https://opt-mainnet.g.alchemy.com/v2/zU15-z2-RLFUMekoGo0TAwJ-XM6KJ2HC",
    "https://optimism.gateway.tenderly.co/63AcdkOsrQlodP3m8COYj",
  ],
  [avalanche.id]: [
    "https://avax-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [zora.id]: [
    "https://zora-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
    "https://rpc-zora-mainnet-0.t.conduit.xyz/E4vxq3C7amB1Su52yFXyKtWKhRiMZkeaS",
  ],
  [blast.id]: [
    "https://blast-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [linea.id]: [
    "https://linea-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [mode.id]: [
    "https://rpc-mode-mainnet-0.t.conduit.xyz/E4vxq3C7amB1Su52yFXyKtWKhRiMZkeaS",
  ],
  [bob.id]: [
    "https://rpc-bob-mainnet-0.t.conduit.xyz/E4vxq3C7amB1Su52yFXyKtWKhRiMZkeaS",
  ],
  [boba.id]: [
    "https://boba-ethereum.gateway.tenderly.co/42t0xRLkQ15ArJlm9PNmrB",
  ],
  [cyber.id]: ["https://cyber.alt.technology"],
  [funkiMainnet.id]: ["https://rpc-mainnet.funkichain.com"],
  [mint.id]: ["https://rpc.mintchain.io"],
  [polygonZkEvm.id]: [
    "https://polygonzkevm-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [scroll.id]: [
    "https://scroll-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [redstone.id]: ["https://rpc.redstonechain.com"],
  [unichain.id]: ["https://mainnet.unichain.org"],
  [hyperevmChain.id]: ["https://rpc.hyperliquid.xyz/evm"],
  [zksync.id]: [
    "https://zksync-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [sxChain.id]: [
    "https://rpc-rollup.sx.technology/infra-partner-http/4cde6fdfad644b55a6f2d47744650572",
    "https://rpc-rollup.sx.technology",
  ],
  [sxToronto.id]: ["https://rpc-rollup.toronto.sx.technology"],
  [assetchainMainnet.id]: ["https://mainnet-rpc.assetchain.org"],
  [rariChain.id]: ["https://mainnet.rpc.rarichain.org/http"],
  [worldChain.id]: [
    "https://worldchain-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
  [berachain.id]: [
    "https://berachain-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
    "https://rpc.berachain.com",
    "https://berachain.gateway.tenderly.co/4iJwT6b3hT6ySqER28qJg3",
  ],
  [ink.id]: [
    "https://ink-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
    "https://rpc-gel.inkonchain.com",
    "https://ink.gateway.tenderly.co/6f3yqKT6eX80p2Om04yrt4",
  ],
  [bsc.id]: [
    "https://bnb-mainnet.g.alchemy.com/v2/95hQzf_-yYsZGaUjmXXF666hsOFxbr76",
  ],
  [monad.id]: [
    "https://monad-mainnet.g.alchemy.com/v2/vnhqvi-ajf5_pf5ZMzPvUS_VF3w4HqlY",
  ],
};

const preferredHttpTransport = (chainId: number) => {
  const urls = preferredRpcUrlsByChainId[chainId];
  if (!urls?.length) return http();
  if (urls.length === 1) return http(urls[0]);
  return fallback(urls.map((url) => http(url)));
};

// Create a function that creates and returns the config
const createWagmiConfig = () => {
  const config = createConfig({
    chains: [
      sepolia,
      base,
      baseSepolia,
      arbitrum,
      arbitrumNova,
      polygon,
      optimism,
      avalanche,
      zora,
      blast,
      fantom,
      gnosis,
      linea,
      mantle,
      mode,
      real,
      manta,
      bob,
      boba,
      cyber,
      funkiMainnet,
      ham,
      lisk,
      mint,
      polygonZkEvm,
      taiko,
      scroll,
      redstone,
      unichain,
      hyperevmChain,
      zksync,
      mainnet,
      polygonAmoy,
      berachain,
      ink,
      sxChain,
      sxToronto,
      assetchainMainnet,
      rariChain,
      worldChain,
      bsc,
      monad,
    ],
    connectors: [
      injected({ target: "metaMask" }),
      metaMask({
        extensionOnly: !isMobile,
        forceInjectProvider: false,
        dappMetadata: {
          name: "Glide",
          url:
            typeof window !== "undefined"
              ? window.location.origin
              : "https://deposit.paywithglide.xyz",
        },
        useDeeplink: isMobile,
        logging: {
          logger(_msg, ...args) {
            logger.info(`${_msg} ${args}`);
          },
        },
        openDeeplink: (url) => {
          logger.info(`[MetaMask] openDeeplink ${url}`);
          window.location.href = url;
        },
        storage: {
          enabled: true,
          storageManager: {
            async persistChannelConfig(channelConfig, context) {
              logger.info(
                `[MetaMask] persistChannelConfig ${JSON.stringify({ channelConfig, context })}`,
              );
              const key = `glide.metamask.channelConfig_${typeof context === "string" ? context : "default"}`;
              await glideLocalStorage.setItem(
                key,
                JSON.stringify(channelConfig),
              );
              return Promise.resolve();
            },
            async persistAccounts(accounts, context) {
              logger.info(
                `[MetaMask] persistAccounts ${JSON.stringify({ accounts, context })}`,
              );
              const key = `glide.metamask.accounts_${typeof context === "string" ? context : "default"}`;
              await glideLocalStorage.setItem(key, JSON.stringify(accounts));
              return Promise.resolve();
            },
            async getCachedAccounts() {
              logger.info("[MetaMask] getCachedAccounts");
              const keys = await glideLocalStorage.getKeys();
              const accountsKeys = keys.filter((key) =>
                key.startsWith("glide.metamask.accounts_"),
              );

              if (accountsKeys.length === 0) return Promise.resolve([]);

              const accountsData = await glideLocalStorage.getItem(
                accountsKeys[0],
              );
              return Promise.resolve(
                accountsData ? JSON.parse(accountsData) : [],
              );
            },
            async getCachedChainId() {
              logger.info("[MetaMask] getCachedChainId");
              const chainIdData = await glideLocalStorage.getItem(
                "glide.metamask.chainId",
              );
              return Promise.resolve(
                chainIdData ? JSON.parse(chainIdData) : undefined,
              );
            },
            async persistChainId(chainId, context) {
              logger.info(
                `[MetaMask] persistChainId ${JSON.stringify({ chainId, context })}`,
              );
              await glideLocalStorage.setItem(
                "glide.metamask.chainId",
                JSON.stringify(chainId),
              );
              return Promise.resolve();
            },
            async getPersistedChannelConfig(options) {
              logger.info(
                `[MetaMask] getPersistedChannelConfig ${JSON.stringify({ options })}`,
              );
              const key = `glide.metamask.channelConfig_${options?.context || "default"}`;
              const configData = await glideLocalStorage.getItem(key);
              return Promise.resolve(
                configData ? JSON.parse(configData) : undefined,
              );
            },
            async terminate(channelId) {
              logger.info(
                `[MetaMask] terminate ${JSON.stringify({ channelId })}`,
              );

              for (const key of await glideLocalStorage.getKeys()) {
                if (key.startsWith("glide.metamask.")) {
                  const val = await glideLocalStorage.getItem(key);
                  if (!channelId || val?.includes(channelId)) {
                    await glideLocalStorage.removeItem(key);
                  }
                }
              }

              return Promise.resolve();
            },
          },
        },
      }),
      walletConnect({
        projectId: "f150040b1563ef4119051068cebb0a17",
        customStoragePrefix: "glide.wc",
        metadata: {
          name: "Glide",
          description:
            "Glide helps you pay for transactions using any token on any chain",
          url:
            typeof window !== "undefined"
              ? window.location.origin
              : "https://deposit.paywithglide.xyz",
          icons: [
            "https://media.decentralized-content.com/-/rs:fit:1920:1920/aHR0cHM6Ly9tYWdpYy5kZWNlbnRyYWxpemVkLWNvbnRlbnQuY29tL2lwZnMvYmFma3JlaWdjZWVsY3Bia3djNGJydmQ3dWpyZ3FlZnBtY3U0cmxhZGRucDRlcW90eGl4NGc2dHc1aWE",
          ],
        },
        storage: {
          async getItem(key) {
            const item = await glideLocalStorage.getItem(key);
            return item ? JSON.parse(item) : undefined;
          },
          async setItem(key, value) {
            await glideLocalStorage.setItem(key, JSON.stringify(value));
          },
          async removeItem(key) {
            await glideLocalStorage.removeItem(key);
          },
          async getKeys() {
            return await glideLocalStorage.getKeys();
          },
          async getEntries<T>(): Promise<[string, T][]> {
            const keys = await glideLocalStorage.getKeys();
            const entries: [string, T][] = [];

            for (const key of keys) {
              const value = await glideLocalStorage.getItem(key);
              if (value) {
                try {
                  entries.push([key, JSON.parse(value) as T]);
                } catch {
                  // If the value can't be parsed as JSON, skip it
                  logger.info(
                    `[walletConnect] Failed to parse value for key: ${key}`,
                  );
                }
              }
            }

            return entries;
          },
        },
      }),
    ],
    transports: {
      [baseSepolia.id]: preferredHttpTransport(baseSepolia.id),
      [sepolia.id]: preferredHttpTransport(sepolia.id),
      [base.id]: preferredHttpTransport(base.id),
      [arbitrum.id]: preferredHttpTransport(arbitrum.id),
      [arbitrumNova.id]: preferredHttpTransport(arbitrumNova.id),
      [polygon.id]: preferredHttpTransport(polygon.id),
      [optimism.id]: preferredHttpTransport(optimism.id),
      [avalanche.id]: preferredHttpTransport(avalanche.id),
      [zora.id]: preferredHttpTransport(zora.id),
      [blast.id]: preferredHttpTransport(blast.id),
      [fantom.id]: preferredHttpTransport(fantom.id),
      [gnosis.id]: preferredHttpTransport(gnosis.id),
      [linea.id]: preferredHttpTransport(linea.id),
      [mantle.id]: preferredHttpTransport(mantle.id),
      [mode.id]: preferredHttpTransport(mode.id),
      [real.id]: preferredHttpTransport(real.id),
      [manta.id]: preferredHttpTransport(manta.id),
      [bob.id]: preferredHttpTransport(bob.id),
      [boba.id]: preferredHttpTransport(boba.id),
      [cyber.id]: preferredHttpTransport(cyber.id),
      [funkiMainnet.id]: preferredHttpTransport(funkiMainnet.id),
      [ham.id]: preferredHttpTransport(ham.id),
      [lisk.id]: preferredHttpTransport(lisk.id),
      [mint.id]: preferredHttpTransport(mint.id),
      [polygonZkEvm.id]: preferredHttpTransport(polygonZkEvm.id),
      [taiko.id]: preferredHttpTransport(taiko.id),
      [scroll.id]: preferredHttpTransport(scroll.id),
      [redstone.id]: preferredHttpTransport(redstone.id),
      [unichain.id]: preferredHttpTransport(unichain.id),
      [hyperevmChain.id]: preferredHttpTransport(hyperevmChain.id),
      [zksync.id]: preferredHttpTransport(zksync.id),
      [mainnet.id]: preferredHttpTransport(mainnet.id),
      [sxChain.id]: preferredHttpTransport(sxChain.id),
      [sxToronto.id]: preferredHttpTransport(sxToronto.id),
      [assetchainMainnet.id]: preferredHttpTransport(assetchainMainnet.id),
      [rariChain.id]: preferredHttpTransport(rariChain.id),
      [worldChain.id]: preferredHttpTransport(worldChain.id),
      [polygonAmoy.id]: preferredHttpTransport(polygonAmoy.id),
      [berachain.id]: preferredHttpTransport(berachain.id),
      [ink.id]: preferredHttpTransport(ink.id),
      [bsc.id]: preferredHttpTransport(bsc.id),
      [monad.id]: preferredHttpTransport(monad.id),
    },

    storage: createStorage({
      key: "glide.wagmi",
      storage: {
        async getItem(name) {
          return await glideLocalStorage.getItem(name);
        },
        async setItem(name, value) {
          await glideLocalStorage.setItem(name, value);
        },
        async removeItem(name) {
          await glideLocalStorage.removeItem(name);
        },
      },
    }),
  });

  // Strip Coinbase before WagmiProvider's default reconnectOnMount runs
  // (see COINBASE_WALLET_RDNS above). restoreCoinbaseConnector() adds it
  // back post-mount for display/manual connect.
  config._internal.connectors.setState((connectors) =>
    connectors.filter((c) => c.id !== COINBASE_WALLET_RDNS),
  );

  return config;
};

// Create a lazy-loaded wagmiConfig using a getter
let _wagmiConfig: ReturnType<typeof createWagmiConfig> | undefined = undefined;

export const wagmiConfig = () => {
  if (!_wagmiConfig) {
    _wagmiConfig = createWagmiConfig();
  }

  return _wagmiConfig;
};

// Re-adds the Coinbase EIP-6963 connector after the mount-time
// reconnectOnMount pass has already run, so it's available for the wallet
// list / manual "Connect" click without ever being auto-reconnected. Safe to
// call from a useEffect; returns an unsubscribe fn for cleanup.
export const restoreCoinbaseConnector = () => {
  const config = wagmiConfig();
  const mipd = config._internal.mipd;
  if (!mipd) return () => {};

  const tryRestore = () => {
    if (config.connectors.some((c) => c.id === COINBASE_WALLET_RDNS)) return;
    const provider = mipd.findProvider({ rdns: COINBASE_WALLET_RDNS });
    if (!provider) return;
    const connector = config._internal.connectors.setup(
      config._internal.connectors.providerDetailToConnector(provider),
    );
    config._internal.connectors.setState((connectors) => [
      ...connectors,
      connector,
    ]);
  };

  tryRestore();
  return mipd.subscribe(tryRestore);
};

declare module "wagmi" {
  interface Register {
    config: typeof wagmiConfig;
  }
}
